import { useMemo, useState } from 'react'
import { ArrowLeft, Download } from 'lucide-react'
import { isAxiosError } from 'axios'

import { Link, useParams } from '@/lib/router'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Kpi } from '@/components/ui/kpi'
import { Skeleton } from '@/components/ui/skeleton'

import { dateTime, num } from '@/lib/format'

import { useBatch, downloadFailedRowsXlsx } from './api'

interface RowError {
  rowNumber: number
  column: string | null
  code: string
  message: string
  rawValue: string | null
}

export function BatchDetailPage() {
  const { batchId } = useParams({ from: '/ingest/$batchId' })
  const { data: batch, isPending, isError, error } = useBatch(batchId)

  const notFound = isError && isAxiosError(error) && error.response?.status === 404

  const rejections = useMemo<RowError[]>(() => {
    if (!batch?.rejectionReportJson) return []
    try { return JSON.parse(batch.rejectionReportJson) as RowError[] }
    catch { return [] }
  }, [batch?.rejectionReportJson])

  // Server truncates the report to the first 500 errors; totalErrorCount
  // carries the real total so ops know how much more the file holds.
  const totalErrors = batch?.totalErrorCount ?? rejections.length
  const truncated = totalErrors > rejections.length

  // One bad column repeated down a 3,600-row file produces 3,600 identical
  // errors. Scrolling them teaches nothing, so lead with the distinct
  // problems and let the per-row table answer "which rows?" underneath.
  const groups = useMemo(() => {
    const byReason = new Map<string, { column: string | null; code: string; message: string; count: number }>()
    for (const r of rejections) {
      const key = `${r.code}|${r.column ?? ''}|${r.message}`
      const seen = byReason.get(key)
      if (seen) seen.count += 1
      else byReason.set(key, { column: r.column, code: r.code, message: r.message, count: 1 })
    }
    return [...byReason.values()].sort((a, b) => b.count - a.count)
  }, [rejections])

  // "Reprocess only failed records": hand ops the failed rows as .xlsx in the
  // shape of the file they uploaded, with the reason appended, so they can fix
  // in place and re-upload. Server-generated, so it carries EVERY failing row
  // — not just the first 500 this screen is capped at.
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  async function onDownloadFailedRows() {
    if (!batchId) return
    setDownloading(true)
    setDownloadError(null)
    try {
      await downloadFailedRowsXlsx(batchId)
    } catch {
      setDownloadError('Could not build the failed-rows file. Try again.')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <Link to="/ingest" className="inline-flex items-center gap-1 text-2xs font-semibold uppercase tracking-wider text-navy-700 hover:underline">
        <ArrowLeft className="h-3 w-3" /> Back to ingestion
      </Link>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-2xs font-semibold uppercase tracking-wider text-navy-600">Ingestion batch</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-navy-900">
            {batch?.fileName ?? <Skeleton className="h-7 w-56" />}
          </h1>
          <p className="mt-1 font-mono text-2xs text-muted-foreground">{batchId}</p>
        </div>
        {batch && <StatusBadgeLarge status={batch.status} />}
      </div>

      {isError && (
        <div className="rounded-md border border-negative/30 bg-negative-soft p-4 text-sm text-negative">
          {notFound
            ? 'This batch does not exist (it may belong to another tenant or the link is stale).'
            : 'Unable to load this batch.'}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi
          label="Kind"
          value={batch ? batch.kind.replace(/_/g, ' ') : '—'}
          hint={batch?.options?.slot ? `Default slot ${batch.options.slot}` : undefined}
          loading={isPending}
          deltaSense="neutral"
        />
        <Kpi label="Rows accepted" value={batch ? num(batch.acceptedCount) : '—'} loading={isPending} deltaSense="higher-is-better" />
        <Kpi label="Rows rejected" value={batch ? num(batch.rejectedCount) : '—'} loading={isPending} deltaSense="lower-is-better" />
        <Kpi label="Total rows" value={batch?.rowCount != null ? num(batch.rowCount) : '—'} loading={isPending} deltaSense="neutral" />
      </div>

      {batch && (
        <Card>
          <CardHeader>
            <CardTitle>File & lineage</CardTitle>
            <CardDescription>SHA-256 idempotency key carried into every emitted event.</CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-x-6 gap-y-2.5 text-sm sm:grid-cols-[10rem_1fr]">
              <DT>File name</DT>           <dd className="font-medium text-navy-900">{batch.fileName}</dd>
              {batch.options?.slot && (<>
                <DT>Default slot</DT>      <dd className="font-medium text-navy-900">{batch.options.slot}</dd>
              </>)}
              <DT>SHA-256</DT>             <dd className="font-mono text-2xs break-all text-navy-900">{batch.fileSha256}</dd>
              <DT>Submitted</DT>           <dd className="tabular-nums text-navy-900">{dateTime(batch.submittedAt)}</dd>
              <DT>Started</DT>             <dd className="tabular-nums text-navy-900">{dateTime(batch.startedAt)}</dd>
              <DT>Completed</DT>           <dd className="tabular-nums text-navy-900">{dateTime(batch.completedAt)}</dd>
              {batch.failureReason && (<>
                <DT>Failure</DT>           <dd className="text-negative">{batch.failureReason}</dd>
              </>)}
            </dl>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
          <div className="space-y-1.5">
            <CardTitle>Rejection report</CardTitle>
            <CardDescription>
              {!batch ? '—' : rejections.length === 0
                ? 'No rejections. Every row was accepted.'
                : truncated
                  ? `${num(totalErrors)} rows rejected — showing the first ${num(rejections.length)}.`
                  : `${num(totalErrors)} row${totalErrors > 1 ? 's' : ''} rejected.`}
            </CardDescription>
          </div>
          {rejections.length > 0 && (
            <div className="flex flex-col items-end gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={downloading}
                onClick={onDownloadFailedRows}
              >
                <Download className="mr-1.5 h-3.5 w-3.5" />
                {downloading ? 'Preparing…' : 'Failed rows XLSX'}
              </Button>
              {downloadError && (
                <span className="text-2xs text-negative">{downloadError}</span>
              )}
            </div>
          )}
        </CardHeader>
        {groups.length > 0 && (
          <CardContent className="pb-0">
            <div className="space-y-2">
              {groups.map((g, idx) => (
                <div
                  key={idx}
                  className="rounded-md border border-negative/30 bg-negative-soft px-4 py-3"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone="negative">{g.code}</Badge>
                    {g.column && (
                      <span className="font-mono text-xs text-navy-900">{g.column}</span>
                    )}
                    <span className="text-2xs uppercase tracking-wider text-muted-foreground">
                      {num(g.count)} {g.count === 1 ? 'row' : 'rows'}
                      {truncated && ' of the first ' + num(rejections.length)}
                    </span>
                  </div>
                  <p className="mt-1.5 line-clamp-4 break-words text-sm text-navy-900" title={g.message}>
                    {g.message}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        )}
        {rejections.length > 0 && (
          <CardContent className="px-0 pb-0 pt-4">
            <p className="px-5 pb-2 text-2xs font-semibold uppercase tracking-wider text-navy-600">
              Row detail
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-navy-50/40 text-left text-2xs uppercase tracking-wider text-navy-700">
                  <tr>
                    <th className="px-5 py-2 font-medium">Row</th>
                    <th className="px-3 py-2 font-medium">Column</th>
                    <th className="px-3 py-2 font-medium">Code</th>
                    <th className="px-3 py-2 font-medium">Message</th>
                    <th className="px-5 py-2 font-medium">Raw value</th>
                  </tr>
                </thead>
                <tbody>
                  {rejections.map((r, idx) => (
                    <tr key={idx} className="border-b border-slate-200 last:border-0 hover:bg-navy-50">
                      <td className="px-5 py-2 tabular-nums text-navy-900">{r.rowNumber}</td>
                      <td className="px-3 py-2 text-navy-900">{r.column ?? '—'}</td>
                      <td className="px-3 py-2"><Badge tone="negative">{r.code}</Badge></td>
                      {/* Older batches can carry a raw DB_ERROR whose message is
                          a whole INSERT statement — clamp so one bad row cannot
                          stretch the table off-screen. */}
                      <td className="px-3 py-2 text-navy-900">
                        <span className="line-clamp-3 block max-w-[32rem] break-words" title={r.message}>
                          {r.message}
                        </span>
                      </td>
                      <td className="px-5 py-2 font-mono text-2xs text-muted-foreground">{r.rawValue ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  )
}

function DT({ children }: { children: React.ReactNode }) {
  return <dt className="text-2xs uppercase tracking-wider text-muted-foreground">{children}</dt>
}

function StatusBadgeLarge({ status }: { status: string }) {
  const tone =
    status === 'DONE' ? 'positive' :
    status === 'PARTIALLY_DONE' ? 'warning' :
    status === 'FAILED' ? 'negative' :
    status === 'RUNNING' || status === 'QUEUED' ? 'info' : 'slate'
  return <Badge tone={tone as 'positive'|'warning'|'negative'|'info'|'slate'}>{status.replace('_', ' ')}</Badge>
}
